import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Captions, VolumeX, MessageSquareOff, Star, Film, Crop, Mic, Wand2,
  Loader2, AlertCircle, Check, X,
} from 'lucide-react';

const CAPABILITIES = [
  { id: 'auto_captions', label: 'Automatic Captions', icon: Captions },
  { id: 'remove_silence', label: 'Remove Silence', icon: VolumeX },
  { id: 'remove_filler_words', label: 'Remove Filler Words', icon: MessageSquareOff },
  { id: 'best_moments', label: 'Best Moments', icon: Star },
  { id: 'scene_detection', label: 'Scene Detection', icon: Film },
  { id: 'smart_reframe', label: 'Smart Reframe', icon: Crop },
  { id: 'speech_based_editing', label: 'Speech-Based Editing', icon: Mic },
  { id: 'audio_cleanup', label: 'Audio Cleanup', icon: Wand2 },
];

/**
 * Unified AI Tools panel for the Main Editor — one entry point for all 8
 * approved V1 AI editing capabilities. Every request goes through the
 * real backend orchestration layer (dedup + cache + bounded retries, see
 * services/editor_ai_orchestrator.py) — NOTHING here is a canned/fake
 * result. Today every capability resolves to an honest
 * "not available"/"pending integration" status because no analysis or
 * transcription provider is connected anywhere in this codebase; the
 * accept/reject review UI is fully wired for the moment one is.
 */
export function EditorAiToolsPanel({ siblingSources, suggestions, onRequestAnalysis, onAcceptSuggestion, onRejectSuggestion }) {
  const [sourceId, setSourceId] = useState(siblingSources[0]?.id || '');
  const [jobs, setJobs] = useState({}); // capability -> {status, error, result}
  const [loadingCap, setLoadingCap] = useState(null);

  const runCapability = async (capability) => {
    if (!sourceId) return;
    setLoadingCap(capability);
    try {
      const job = await onRequestAnalysis(capability, sourceId, {});
      setJobs((prev) => ({ ...prev, [capability]: job }));
    } finally {
      setLoadingCap(null);
    }
  };

  return (
    <div className="space-y-3" data-testid="ai-tools-panel">
      <div className="space-y-1.5">
        <label className="text-xs text-[#8A93A6]">Analyze which source?</label>
        <Select value={sourceId} onValueChange={setSourceId}>
          <SelectTrigger className="h-8 bg-[#1C1F26] border-white/10 text-white text-sm" data-testid="ai-tools-source-select"><SelectValue placeholder="Choose a source" /></SelectTrigger>
          <SelectContent>{siblingSources.map((s) => <SelectItem key={s.id} value={s.id}>{s.title || s.id}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        {CAPABILITIES.map(({ id, label, icon: Icon }) => {
          const job = jobs[id];
          const capSuggestions = (suggestions || []).filter((s) => s.capability === id && s.content_source_id === sourceId);
          return (
            <div key={id} className="p-2.5 rounded-lg bg-white/[0.03] border border-white/10 space-y-1.5" data-testid={`ai-tool-row-${id}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-2 text-xs text-white"><Icon className="h-3.5 w-3.5 text-[#8A93A6]" />{label}</span>
                <Button
                  size="sm" variant="outline" className="h-7 text-[10px] border-white/10 shrink-0"
                  disabled={!sourceId || loadingCap === id}
                  onClick={() => runCapability(id)}
                  data-testid={`ai-tool-run-${id}`}
                >
                  {loadingCap === id ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}
                  {loadingCap === id ? 'Checking…' : 'Run'}
                </Button>
              </div>

              {job && job.status !== 'completed' && (
                <p className="flex items-start gap-1.5 text-[10px] text-amber-400/80" data-testid={`ai-tool-status-${id}`}>
                  <AlertCircle className="h-3 w-3 shrink-0 mt-0.5" />
                  {job.error || (job.status === 'pending_integration' ? 'Pending integration — no provider connected yet.' : `Status: ${job.status}`)}
                </p>
              )}
              {job && job.status === 'completed' && capSuggestions.length === 0 && (
                <p className="text-[10px] text-[#555E6E]" data-testid={`ai-tool-no-suggestions-${id}`}>Analysis complete — no suggestions returned.</p>
              )}

              {capSuggestions.map((s) => (
                <div key={s.id} className="flex items-center justify-between p-1.5 rounded bg-white/5" data-testid={`ai-suggestion-row-${s.id}`}>
                  <div className="min-w-0">
                    <p className="text-[11px] text-white truncate">{s.label || s.kind}</p>
                    {s.start_seconds != null && <p className="text-[10px] text-[#8A93A6]">{s.start_seconds.toFixed(1)}s – {(s.end_seconds ?? s.start_seconds).toFixed(1)}s{s.score != null ? ` · score ${s.score.toFixed(2)}` : ''}</p>}
                  </div>
                  {s.status === 'pending' ? (
                    <div className="flex gap-1 shrink-0">
                      <button onClick={() => onAcceptSuggestion(s.id)} className="p-1 rounded bg-green-500/10 text-green-400 hover:bg-green-500/20" data-testid={`ai-suggestion-accept-${s.id}`}><Check className="h-3 w-3" /></button>
                      <button onClick={() => onRejectSuggestion(s.id)} className="p-1 rounded bg-red-500/10 text-red-400 hover:bg-red-500/20" data-testid={`ai-suggestion-reject-${s.id}`}><X className="h-3 w-3" /></button>
                    </div>
                  ) : (
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full shrink-0 ${s.status === 'accepted' ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>{s.status}</span>
                  )}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
